import {
  CaraWorkspaceEffortLevel,
  CaraWorkspaceOutputFormat,
  type CaraWorkspaceFileInput,
  type DocumentType
} from '~/api/graphql';
import {
  emptyFileInputChange,
  hasFileInputContent,
  mergeFileInputChanges,
  type FileInputChange
} from '~/components/file';
import { type CopilotWorkspaceCreateOptions } from '~/components/presets/copilot-launcher';
import { renameUploadedFiles } from '~/components/presets/presets.utils';
import {
  type DeliverableChoice,
  type RunCardTemplateKind,
  type UseCaseSpec
} from './use-case-catalog';

export type { RunCardTemplateKind };

export type RunCardTemplate = {
  id: string;
  title: string;
  kind: RunCardTemplateKind;
};

export type RunCardState = {
  lanes: FileInputChange[];
  instructions: string;
  fields: Record<string, string>;
  options: Record<string, boolean>;
  scope: string[];
  documentIds: DocumentType[];
  deliverableKey?: string;
  template?: RunCardTemplate;
  effortLevel?: CaraWorkspaceEffortLevel;
};

export type RunCardSubmission = {
  message: string;
  files: CaraWorkspaceFileInput[];
  options: CopilotWorkspaceCreateOptions;
};

export const expandLanes = (spec: UseCaseSpec, repeatCount: number) =>
  spec.sources.flatMap((lane) => {
    if (!lane.repeatable) {
      return [{ ...lane, label: lane.label, filePrefix: lane.filePrefix }];
    }
    // A lone box needs no number to tell it apart, and the prefix still carries
    // one so the prompt reads the same grouping either way.
    return Array.from({ length: repeatCount }, (_, index) => ({
      ...lane,
      label: repeatCount === 1 ? lane.label : `${lane.label} ${index + 1}`,
      filePrefix: `${lane.filePrefix}${index + 1}] `
    }));
  });

/** Boxes a card opens with, kept next to `expandLanes` so the two cannot drift. */
export const initialRepeatCount = (spec: UseCaseSpec) =>
  Math.max(
    1,
    ...spec.sources.filter((lane) => lane.repeatable).map((lane) => lane.repeatInitial ?? 1)
  );

export const initialRunCardState = (
  spec: UseCaseSpec,
  repeatCount: number,
  deliverableKey?: string
): RunCardState => ({
  lanes: expandLanes(spec, repeatCount).map(() => emptyFileInputChange()),
  instructions: '',
  fields: {},
  options: Object.fromEntries((spec.options ?? []).map((o) => [o.key, o.defaultOn])),
  scope: spec.scope ? [...spec.scope.defaults] : [],
  documentIds: [],
  deliverableKey:
    deliverableKey ?? spec.deliverable?.defaultKey ?? spec.deliverable?.choices[0]?.key
});

export const selectedDeliverable = (
  spec: UseCaseSpec,
  key: string | undefined
): DeliverableChoice | undefined =>
  spec.deliverable?.choices.find((choice) => choice.key === key) ?? spec.deliverable?.choices[0];

/** Hidden fields stay out of the message so a stale certificate holder cannot ride along. */
export const visibleFields = (spec: UseCaseSpec, documentIds: DocumentType[]) =>
  (spec.fields ?? []).filter(
    (field) =>
      !field.requiresDocuments ||
      field.requiresDocuments.some((documentType) => documentIds.includes(documentType))
  );

export const runCardBlockedReason = (
  spec: UseCaseSpec,
  state: RunCardState,
  repeatCount: number
): string | undefined => {
  if (spec.documents && state.documentIds.length === 0) {
    return spec.documents.requiredError;
  }
  const deliverable = selectedDeliverable(spec, state.deliverableKey);
  if (deliverable?.template && !state.template) {
    return 'Choose a template';
  }
  if (spec.requires === 'sources' && !state.lanes.some(hasFileInputContent)) {
    return 'Add at least one document';
  }
  if (deliverable?.requiresLane) {
    const lanes = expandLanes(spec, repeatCount);
    const index = lanes.findIndex((lane) => lane.filePrefix === deliverable.requiresLane);
    const lane = state.lanes[index];
    if (index >= 0 && !(lane && hasFileInputContent(lane))) {
      return `Attach the ${lanes[index].label.toLowerCase()}`;
    }
  }
  if (spec.requires === 'instructions' && !state.instructions.trim()) {
    return `Fill in ${spec.instructions.label.toLowerCase()}`;
  }
  return undefined;
};

/**
 * Camel-cases a message-line label so the backend can read the same field off
 * `presetMetadata` (`Renewal Supplied` → `renewalSupplied`).
 */
export const presetMetadataKey = (label: string): string => {
  const parts = label.split(/[^a-zA-Z0-9]+/).filter(Boolean);
  return parts
    .map((part, index) => {
      const lower = part.toLowerCase();
      return index === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
};

/**
 * Prompts parse delivery format, classification, and toggles from body lines;
 * some matchers are start-anchored, hence the leading/trailing split. File
 * prefixes distinguish roles once both files are attachments on the same workspace.
 *
 * The same fields are copied onto `presetMetadata` under camelCase keys so the
 * API can branch without searching the body. Body lines stay for the model.
 *
 * `state.effortLevel` is sent as given, so pass the level `useEffortLevel`
 * resolved: it drops a level the server would refuse.
 */
export const buildRunCardSubmission = (
  spec: UseCaseSpec,
  state: RunCardState,
  repeatCount: number
): RunCardSubmission => {
  const lanes = expandLanes(spec, repeatCount);
  const files = lanes.flatMap((lane, index) =>
    renameUploadedFiles(state.lanes[index]?.files ?? [], lane.filePrefix)
  );
  // Uploads carry their lane through the file-name prefix; pins carry the same
  // prefix as their role so a multi-lane prompt can place them too.
  const { amsContext, amsRefs = [] } = mergeFileInputChanges(
    ...lanes.map((lane, index) => {
      const laneState = state.lanes[index] ?? emptyFileInputChange();
      return {
        files: [],
        amsContext: laneState.amsContext.map((pin) => ({ ...pin, role: lane.filePrefix })),
        amsRefs: (laneState.amsRefs ?? []).map((pin) => ({ ...pin, role: lane.filePrefix }))
      };
    })
  );

  const leading: string[] = [];
  const trailing: string[] = [];
  const presetMetadata: Record<string, unknown> = { ...spec.presetMetadata };

  for (const line of spec.messageLines ?? []) {
    (line.position === 'leading' ? leading : trailing).push(`${line.key}: ${line.value}`);
    presetMetadata[presetMetadataKey(line.key)] = line.value;
  }

  // One line per key rather than per lane, so a repeatable lane cannot answer
  // the same question several times over. Any lane holding a file or AMS pin makes it yes.
  const supplied = new Map<string, boolean>();
  lanes.forEach((lane, index) => {
    if (!lane.suppliedMessageKey) {
      return;
    }
    const laneState = state.lanes[index];
    const has = !!laneState && hasFileInputContent(laneState);
    supplied.set(lane.suppliedMessageKey, supplied.get(lane.suppliedMessageKey) || has);
  });
  for (const [key, has] of supplied) {
    const value = has ? 'yes' : 'no';
    trailing.push(`${key}: ${value}`);
    presetMetadata[presetMetadataKey(key)] = value;
  }

  const deliverable = selectedDeliverable(spec, state.deliverableKey);
  if (spec.deliverable?.messageKey && deliverable?.messageValue) {
    const line = `${spec.deliverable.messageKey}: ${deliverable.messageValue}`;
    (spec.deliverable.messagePosition === 'leading' ? leading : trailing).push(line);
    presetMetadata[presetMetadataKey(spec.deliverable.messageKey)] = deliverable.messageValue;
  }

  for (const option of spec.options ?? []) {
    const value = state.options[option.key] ? 'enabled' : 'disabled';
    trailing.push(`${option.messageKey}: ${value}`);
    presetMetadata[presetMetadataKey(option.messageKey)] = value;
  }

  if (spec.scope && state.scope.length > 0) {
    const value = state.scope.join(', ');
    trailing.push(`${spec.scope.messageKey}: ${value}`);
    presetMetadata[presetMetadataKey(spec.scope.messageKey)] = value;
  }

  const bodyParts: string[] = [];
  const instructions = state.instructions.trim();
  const body = instructions || spec.defaultMessage || '';
  if (body) {
    bodyParts.push(spec.bodyPrefix ? `${spec.bodyPrefix}:\n\n${body}` : body);
  } else if (spec.bodyPrefix) {
    bodyParts.push(spec.bodyPrefix);
  }

  for (const field of visibleFields(spec, state.documentIds)) {
    const value = (state.fields[field.key] ?? '').trim() || field.emptyFallback;
    if (!value) {
      continue;
    }
    bodyParts.push(
      field.messageHeading
        ? `**${field.messageHeading}**\n\n${value}`
        : `${field.messageKey ?? field.label}: ${value}`
    );
    if (!field.messageHeading) {
      presetMetadata[presetMetadataKey(field.messageKey ?? field.label)] = value;
    }
  }

  const message = [leading.join('\n'), bodyParts.join('\n\n'), trailing.join('\n')]
    .filter(Boolean)
    .join('\n\n');

  const options: CopilotWorkspaceCreateOptions = {
    useCase: spec.id,
    presetOverride: spec.preset,
    documentIds: state.documentIds,
    presetMetadata: Object.keys(presetMetadata).length > 0 ? presetMetadata : spec.presetMetadata
  };

  if (deliverable?.outputFormat && deliverable.outputFormat !== CaraWorkspaceOutputFormat.Text) {
    options.outputFormat = deliverable.outputFormat;
  }

  if (state.effortLevel) {
    options.effortLevel = state.effortLevel;
  }

  if (deliverable?.template && state.template) {
    // Excel and custom forms are requested by id only; naming an output format would render the wrong artifact.
    switch (state.template.kind) {
      case 'word':
        options.outputFormat = CaraWorkspaceOutputFormat.WordTemplate;
        options.wordDocumentTemplateIds = [state.template.id];
        break;
      case 'html':
        options.outputFormat = CaraWorkspaceOutputFormat.HtmlTemplate;
        options.htmlDocumentTemplateIds = [state.template.id];
        break;
      case 'excel':
        options.excelSpreadsheetTemplateIds = [state.template.id];
        break;
      case 'customForm':
        options.customFormIds = [state.template.id];
        break;
    }
  }

  if (amsContext.length > 0) {
    options.amsContext = amsContext;
  }
  if (amsRefs.length > 0) {
    options.amsRefs = amsRefs;
  }

  return { message, files, options };
};
